
	import {
		Component,
		Vue,
		Prop,
		Watch,
		Emit,
		Mixins
	} from "vue-property-decorator";
    import { namespace } from "vuex-class";
    import { Guard, GuardType, PatrolZone, AlarmFilter, UploadedGuard, AddMultipleGuardsDTO } from '@/store/dispatch/types';
    import { Select } from "@sureview/vuejs-sv-dropdown";
    import DragDropUpload from "@/components/form/DragDropUpload.vue";
    import VuePerfectScrollbar from "vue-perfect-scrollbar";
    import {ActionState} from "@/types/ActionState";
    import BulkUploadRow from "./BulkUploadRow.vue";
    import api from "../../services/api.service";
        
    const Dispatch = namespace('dispatch');

     @Component({
         components: {
			"drag-drop-upload": DragDropUpload,
            "vue-perfect-scrollbar": VuePerfectScrollbar,
            "bulk-upload-row": BulkUploadRow
		}
     })
    export default class BulkUploadDialog extends Vue {
        @Dispatch.State (state => state.BulkUploadDialogOpen) bulkUploadDialogOpen: boolean;
        @Dispatch.Mutation setBulkUploadDialogOpen:(open: boolean) => void;

        @Prop({ type: Array, default: () => [] }) availableZones: PatrolZone[];
        @Prop({ type: Array, default: () => [] }) currentGuards: Guard[];

        public hasUploaded: boolean = false;
        public newGuards: UploadedGuard[] = [];

        public uploadState: ActionState = ActionState.Undefined;
        public bulkUploadTemplateUrl: string = "";

        // Lifecycle hook for component created
        private created() {
            const templateHeader:string = "CallSign,Type,Zone,AlarmQueueFilter\n";
            const templateSampleData="Guard Name 1, Static, Main Building, Floor 2"
			const templateData = templateHeader + templateSampleData;
			const bulkUploadTemplateBlob = new Blob([templateData], { type: "text/csv" });
			this.bulkUploadTemplateUrl = window.URL.createObjectURL(bulkUploadTemplateBlob);
		}

        // Lifecycle hook for component destroyed
		private destroyed() {
			window.URL.revokeObjectURL(this.bulkUploadTemplateUrl);
		}

        // Resets the data
        private resetData() {
            this.hasUploaded = false;
            this.newGuards = [];
            this.uploadState = ActionState.Undefined;
        }

        // Checks if all the data is valid - used to enable the upload button
        public get dataValid(): boolean {     
            let dataValid: boolean = true;

            this.newGuards.every(newGuard => {
                if (!newGuard.callsignValid || !newGuard.dispatchGuardTypeValid || 
                    !newGuard.patrolZoneValid || !newGuard.alarmQueueFilterValid) {        
                    dataValid = false;
                }
            });

            // Return true if a file has been uploaded and the data is valid
            return dataValid && this.hasUploaded;
        }

        // Opens the dialog when the state changes
        @Watch("bulkUploadDialogOpen")
        private onbulkUploadDialogOpen(value: boolean, oldValue: boolean) {
            if (value) {
                // open - reset the data as could have been opened previously
                this.resetData();
            } 
        }

        // Handles the cancel click 
        private cancel() {
            this.setBulkUploadDialogOpen(false);
        }

        // Maps the data in the table of new guard details to the DTO used by the api
        private mapNewGuardsTableToDto() : AddMultipleGuardsDTO {
            const guardsToAdd: AddMultipleGuardsDTO = {guards:[]};
            
            this.newGuards.forEach(newGuard => {
                const guardToAdd: Guard = {
                    callsign: newGuard.callsign,
                    dispatchGuardTypeID: newGuard.dispatchGuardTypeID,
                    alarmQueueFilterID: newGuard.alarmQueueFilterID,
                    alarmQueueFilterTitle: newGuard.alarmQueueFilterTitle,
                    patrolZones: newGuard.patrolZones
                };
                guardsToAdd.guards.push(guardToAdd);
            });

            return guardsToAdd;
        }

        // Performs the upload
        private async uploadGuards(): Promise<void> {
            // Map the table of guard details to the DTO
            const guardsToAdd: AddMultipleGuardsDTO = this.mapNewGuardsTableToDto();
            
            this.uploadState = ActionState.InProcess;
            await api.addMultipleGuards(guardsToAdd);
            this.uploadState = ActionState.Success;
        
            // Close the dialog
            this.setBulkUploadDialogOpen(false);

             // Emit an event the parent will list for to trigger a refresh
            this.$emit("refresh", null);          
        }

        // Handles an invalid file format being opened
        private onInvalidFileDropped(file: File): void {
			this.$notify({
				type: "error",
				title: "Call Signs - Bulk Upload",
				text: `Unable to upload the file provided (${file.name}) as it was not a valid CSV file`
			});
        }
        
        // Performs the processing of the uploaded file
        private async fileUpload(file: File, progressHandler: (number) => void): Promise<UploadedGuard[]> {
            const response = await api.uploadGuardsFile(file, progressHandler);
            return response.data;
        }
        
        // Called once the file upload is complete
        private onFileUploaded(file: File, newGuards: UploadedGuard[]): void {
            this.hasUploaded = true;
            this.newGuards = newGuards;
        }
        
        // Checks if all callsigns in the table are unique - called each time a callsign is changed
        private revalidateCallsigns(): void {
             this.newGuards.forEach(newGuard => {
                // Check the table of new guards
                const duplicateInUploadedData: Boolean = 
                    this.newGuards.filter(g=> g.callsign == newGuard.callsign).length > 1;
                
                // Check the list of existing guards
                const duplicateInCurrentGuards: Boolean = 
                    this.currentGuards.filter(g=> g.callsign == newGuard.callsign).length > 0;

                if (duplicateInUploadedData || duplicateInCurrentGuards) {
                    newGuard.callsignValid = false;
                } else {
                     newGuard.callsignValid = true;
                }
             });
        }
        
        // removes a row from the array of uploaded guards
        private deleteRow(rowIndex:number): void {
            this.newGuards.splice(rowIndex,1);

            // Revalidate the callsigns
            this.revalidateCallsigns();

            // if there are now no entries in the table then it is as if no file has been uploaded
            // Reset the flag so that a file can be uploaded 
            if (this.newGuards.length === 0) {
                this.hasUploaded = false;
            }
        }

        // Gets the enum so it can be referenced in the template
        public get actionState (): typeof ActionState {
            return ActionState;
        }
    }
